import type Highcharts from 'highcharts/highstock';
import type { ChartRenderCallbackFunction, Options } from 'highcharts/highstock';
import moment from 'moment/moment';
import { chartColors, CLIENT_OUTPUT_Y_AXIS_OFFSET, FONT_FAMILY_VERLAG, FONT_WEIGHT_REGULAR, X_AXIS_OFFSET } from '../../../config/constants';
import type { HighchartCallbackFn, IClientPackageCashflowParams } from '../../../interfaces/projection';

type CashflowLegendItem = {
    symbol?: Highcharts.SVGElement;
    line?: Highcharts.SVGElement;
    label?: Highcharts.SVGElement;
};

const alignLegendMarkerToLabel = (series: Highcharts.Series) => {
    const legendItem = (series as Highcharts.Series & { legendItem?: CashflowLegendItem }).legendItem;
    const label = legendItem?.label;
    if (!label) {
        return;
    }
    const baseline = Number(label.attr('y'));
    if (!Number.isFinite(baseline)) {
        return;
    }
    const fontSize = series.chart.renderer.fontMetrics(label.element as SVGElement).f;
    if (!fontSize) {
        return;
    }
    const centerY = baseline - fontSize * 0.4;
    [legendItem.line, legendItem.symbol].forEach((element) => {
        if (!element) {
            return;
        }
        const box = element.getBBox();
        const currentTranslateY = Number(element.attr('translateY') || 0);
        const delta = centerY - (box.y + box.height / 2);
        if (Math.abs(delta) > 0.5) {
            element.attr({ translateY: currentTranslateY + delta });
        }
    });
};

const alignCashflowLegend = (chart: Highcharts.Chart) => {
    chart.series.forEach(alignLegendMarkerToLabel);
};

export const getCashflowOptions = (params: IClientPackageCashflowParams): Options => ({
    chart: {
        animation: false,
        events: {
            load(this: Highcharts.Chart) {
                params.onChartLoadEventHandlers.forEach((handler) => {
                    handler(this);
                });
            },
            render: function (this: Highcharts.Chart) {
                const xAxis = this.xAxis[0];
                // @ts-expect-error Problems with missing Highcharts types
                params.refs.xAxisYPosition.current = xAxis['bottom'] - xAxis['offset'];
                alignCashflowLegend(this);
                params.onChartRenderEventHandlers.forEach((handler: HighchartCallbackFn<Highcharts.Chart>) => {
                    handler(this);
                });
            } as ChartRenderCallbackFunction,
        },
        spacingLeft: 5,
        spacingTop: 45,
        backgroundColor: '#fff',
    },
    accessibility: { enabled: false },
    credits: { enabled: false },
    title: { text: '', margin: 0 },
    yAxis: {
        offset: CLIENT_OUTPUT_Y_AXIS_OFFSET,
        gridLineColor: chartColors.GRID_LINE_CLIENT_OUTPUT,
        gridLineWidth: 2,
        tickAmount: 10,
        maxPadding: 0.1,
        ...(params.yAxisMin !== undefined && { min: params.yAxisMin }),
        ...(params.yAxisMax !== undefined && { max: params.yAxisMax }),
        plotLines: [
            {
                value: 0,
                width: 0,
                zIndex: 999,
            },
        ],
        title: {
            text: '',
        },
        labels: {
            style: {
                color: chartColors.LABEL_CLIENT_OUTPUT,
                fontSize: '1.5rem',
                fontFamily: FONT_FAMILY_VERLAG,
            },
            y: -10,
        },
    },
    xAxis: {
        crosshair: false,
        tickLength: 10,
        className: 'highcharts-client-output-x-axis',
        startOnTick: false,
        offset: 2 * X_AXIS_OFFSET,
        labels: {
            formatter: params.xAxisLabelsFormatterFn,
            style: {
                color: chartColors.LABEL_CLIENT_OUTPUT,
                fontFamily: FONT_FAMILY_VERLAG,
                fontSize: params.xAxisFontSize || '1rem',
            },
            y: 40,
        },
        type: 'datetime',
        units: [['year', null]],
        min: moment(params.yearRange.from, 'yyyy').valueOf(),
        max: moment(params.yearRange.to, 'yyyy').valueOf(),
    },
    legend: {
        verticalAlign: 'bottom',
        margin: 80,
        itemDistance: 40,
        symbolWidth: 16,
        symbolHeight: 16,
        squareSymbol: true,
        symbolPadding: 10,
        align: 'left',
        itemStyle: {
            fontSize: params.legendFontSize || '1rem',
            fontFamily: FONT_FAMILY_VERLAG,
            fontWeight: `${FONT_WEIGHT_REGULAR}`,
        },
    },
    series: params.seriesData,
    plotOptions: {
        column: {
            grouping: false,
            shadow: false,
            borderWidth: 0,
            legendSymbol: 'rectangle',
        },
        line: {
            lineWidth: 3,
            legendSymbol: 'lineMarker',
        },
        series: {
            animation: {
                duration: 0,
            },
            marker: { symbol: 'circle' },
            pointStart: moment(params.yearRange.from).valueOf(),
            pointIntervalUnit: 'year',
            events: {
                legendItemClick: () => false,
            },
        },
    },
});
